import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TableCell, TableRow } from '@/components/ui/table';
import { ProductWithVariantsStock } from '@/types/product-type';
import { ChevronDown, ChevronRight, Edit, MoreHorizontal, PackagePlus, Send, Trash2 } from 'lucide-react';
import { ProductStockStats } from './product-stock-utils';

interface ProductTreeRowContentProps {
  product: ProductWithVariantsStock;
  stats: ProductStockStats;
  isOpen: boolean;
  onToggle: () => void;
  onEdit: (e: React.MouseEvent) => void;
  onReceiveStock: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  onToggleStorefront?: (e: React.MouseEvent) => void;
  isUpdatingStorefront?: boolean;
}

export function ProductTreeRowContent({
  product,
  stats,
  isOpen,
  onToggle,
  onEdit,
  onReceiveStock,
  onDelete,
  onToggleStorefront,
  isUpdatingStorefront,
}: ProductTreeRowContentProps) {
  const { totalStockInHand, variantCount, stockStatus } = stats;

  return (
    <TableRow
      onClick={onToggle}
      className={`
        transition-all duration-150
        border-b border-slate-200/60
        group cursor-pointer
        ${product.isActive === false
          ? 'bg-slate-50 hover:bg-slate-100/50'
          : 'bg-white hover:bg-blue-50/40'
        }
        ${isOpen ? 'bg-blue-50/30' : ''}
      `}
    >
      {/* Product Column */}
      <TableCell className="py-2 px-2 sm:py-2.5 sm:px-3 min-w-0">
        <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
          <div className="shrink-0 w-5 sm:w-6 flex items-center justify-center">
            {isOpen ? (
              <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
            ) : (
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
            )}
          </div>
          <Avatar className="h-8 w-8 sm:h-10 sm:w-10 ring-1 ring-slate-100 shrink-0">
            <AvatarImage src={product.productImageUrl || undefined} />
            <AvatarFallback className="bg-gradient-to-br from-blue-50 to-indigo-100 text-slate-600 font-semibold text-xs sm:text-sm">
              {product.productName.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900 text-sm sm:text-base leading-tight flex items-center gap-2 flex-wrap">
              <span className="truncate">{product.productName}</span>
              {product.categoryName && (
                <span className="inline-flex items-center text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                  {product.categoryName}
                </span>
              )}
              {onToggleStorefront && (
                <button
                  type="button"
                  disabled={isUpdatingStorefront}
                  onClick={onToggleStorefront}
                  title={
                    product.onStoreFront
                      ? 'Displayed on Telegram Mini App (click to remove)'
                      : 'Click to display on Telegram Mini App'
                  }
                  className={`inline-flex items-center gap-1 text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium border transition-colors ${
                    product.onStoreFront
                      ? 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100'
                      : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100 hover:text-slate-700'
                  }`}
                >
                  <Send className="h-2.5 w-2.5" />
                  <span>{product.onStoreFront ? 'Telegram Mini App' : '+ Telegram Mini App'}</span>
                </button>
              )}
            </div>
            <div className="flex items-center flex-wrap gap-1 sm:gap-1.5 mt-0.5">
              <div className="text-xs sm:text-sm text-slate-500">
                {variantCount} variant{variantCount !== 1 ? 's' : ''}
              </div>
              <div className="text-xs sm:text-sm text-slate-400 hidden sm:inline">•</div>
              <div className="text-xs sm:text-sm text-slate-500">
                {totalStockInHand.toLocaleString()}
              </div>
              <span
                className={`text-xs sm:text-sm px-1.5 sm:px-2 py-0.5 rounded-full font-medium ${stockStatus === 'Out of Stock'
                  ? 'bg-red-100 text-red-800'
                  : stockStatus === 'Low Stock'
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-green-100 text-green-800'
                  }`}
              >
                {stockStatus}

              </span>
            </div>
          </div>
        </div>
      </TableCell>

      {/* Stock Movement Column - Aggregated */}
      <TableCell className="py-2 px-1.5 sm:px-2 text-center min-w-0">
        <div>
          ~
        </div>

        {product.isActive === false ? (
          <span className="shrink-0 text-[10px] sm:text-xs px-1.5 py-0.5 rounded border border-slate-200 bg-slate-100 text-slate-500">
            Inactive
          </span>
        ) : (
          <span className="shrink-0 text-[10px] sm:text-xs px-1.5 py-0.5 rounded border border-green-200 bg-green-100 text-green-600">
            Active
          </span>
        )}
      </TableCell>

      {/* Price Column - Hidden on mobile */}
      <TableCell className="hidden sm:table-cell py-1.5 px-2 text-right min-w-0">
        <span className="text-sm sm:text-base font-bold text-green-700">
          {/* {product.productPrice ? formatPrice(product.productPrice) : 'N/A'} */}~
        </span>
      </TableCell>

      {/* Actions Column */}
      <TableCell className="py-2 px-3 text-center" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2 gap-1.5 border-slate-300 bg-white text-slate-600 hover:bg-slate-100 hover:border-slate-400 hover:text-slate-900 shadow-sm transition-all group-hover:border-blue-300 group-hover:text-blue-700"
            >
              <MoreHorizontal className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline text-xs font-medium">Actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            {onToggleStorefront && (
              <DropdownMenuItem onClick={onToggleStorefront} disabled={isUpdatingStorefront}>
                <Send className="mr-2 h-4 w-4 text-sky-600" />
                {product.onStoreFront ? 'Remove from Telegram Mini App' : 'Show on Telegram Mini App'}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onReceiveStock}>
              <PackagePlus className="mr-2 h-4 w-4" />
              Receive Stock
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-red-600">
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
